'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import * as RDialog from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';
import { isFromGlobalOverlay } from '../../lib/global-overlay';
import { getModalHost, registerModalStackEntry } from '../../lib/modal-stack';
import { InsideModalProvider, PortalContainerProvider } from '../../lib/portal-container';
import { SFooter, type SFooterButton } from '../SFooter';
import { SOverlayHeader } from '../SOverlayHeader';

const CLOSE_ANIMATION_DURATION = 300;

/**
 * 너비 조절에서 방향키 한 번에 움직이는 양(px). Shift 를 함께 누르면 large.
 * SGnb·SSplitter 의 px 단계와 같은 값이라 세 컴포넌트의 체감이 같다.
 */
const RESIZE_KEY_STEP = { normal: 10, large: 50 };

/**
 * 조절선 히트 영역 폭. 겉보기 선(--cmp-splitter-border-width) 양옆에 잡기 여유를 둔다.
 * 패널이 overflow:hidden 이라 밖으로 넘길 수 없어 안쪽으로만 넓힌다 — SGnb 와 같다.
 */
const RESIZE_HIT_WIDTH =
  'calc(var(--cmp-splitter-border-width) + var(--cmp-splitter-border-padding) * 2)';

function toPx(value: number | string | undefined, percentBase: number): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  if (normalized.endsWith('px')) {
    const numeric = Number(normalized.slice(0, -2));
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (normalized.endsWith('%')) {
    const numeric = Number(normalized.slice(0, -1));
    return Number.isFinite(numeric) ? (percentBase * numeric) / 100 : undefined;
  }
  if (normalized.endsWith('vw')) {
    const numeric = Number(normalized.slice(0, -2));
    return Number.isFinite(numeric) ? (window.innerWidth * numeric) / 100 : undefined;
  }
  if (normalized.endsWith('vh')) {
    const numeric = Number(normalized.slice(0, -2));
    return Number.isFinite(numeric) ? (window.innerHeight * numeric) / 100 : undefined;
  }
  return undefined;
}

function clamp(value: number, min?: number, max?: number) {
  const minValue = min ?? Number.NEGATIVE_INFINITY;
  const maxValue = max ?? Number.POSITIVE_INFINITY;
  return Math.min(Math.max(value, minValue), maxValue);
}

export type SDrawerButton = SFooterButton;

export interface SDrawerProps {
  /** 표시 여부 */
  open?: boolean;
  /** 표시 상태 변경 */
  onOpenChange?: (open: boolean) => void;
  /** 닫기(X) 버튼 클릭 */
  onClose?: () => void;
  /**
   * backdrop·ESC로 닫히지 않고 흔들림 효과를 준다.
   * **기본값 true** — Drawer는 내용을 작성·구성하는 곳이라 임의 닫힘을 막는 것이 기본이다.
   * 닫기 경로는 X 버튼과 footer 버튼뿐이다. false로 주면 backdrop·ESC 닫기가 열린다.
   */
  persistent?: boolean;
  /** 접근성 제목 및 헤더 제목 */
  title?: string;
  /**
   * Drawer 너비. 기본값은 Figma drawer 기준 572px이다.
   * 창이 이보다 좁으면 창 폭까지만 넓어진다 — 드로어가 화면 밖으로 나가지 않는다.
   * `resizable` 로 조절한 폭은 이 값이 바뀔 때 되돌아간다 — 앱이 폭을 저장해 두었다가
   * 다시 열 때 넘겨주면 그 폭으로 열린다.
   */
  width?: number | string;
  /**
   * 왼쪽 테두리를 끌어 너비를 조절할 수 있게 한다. 조절선은 패널 왼쪽 경계 전체이며
   * 평소엔 보이지 않다가 hover·포커스·조절 중에만 드러난다(SSplitter·SGnb 와 같은 선).
   * 포커스를 받아 방향키(Shift 는 크게)·Home·End 로도 조절된다.
   */
  resizable?: boolean;
  /** 리사이즈 가능한 최소 너비(px, %, vw, vh). % 는 viewport 기준이며, 창 폭보다 클 수 없다 */
  minWidth?: number | string;
  /** 리사이즈 가능한 최대 너비(px, %, vw, vh). % 는 viewport 기준이며, 주지 않으면 창 폭이 상한이다 */
  maxWidth?: number | string;
  /** 너비가 확정될 때(드래그를 놓거나 방향키 조작). 드래그하는 동안에는 오지 않는다 */
  onWidthChange?: (width: number) => void;
  /** footer 좌측 슬롯 */
  footerLeft?: ReactNode;
  /** 우측 기본 액션 버튼 */
  button?: SDrawerButton;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * SDrawer — Figma drawer 포팅.
 *
 * 모달과 동일한 전역 modal host와 stack을 공유한다. 따라서 Drawer가 열린 상태에서
 * 모달을 추가로 열면 새 모달이 Drawer 위에 쌓이고, Drawer 내부 floating은 전용 portal
 * container를 통해 Drawer의 backdrop 위에 표시된다.
 */
export function SDrawer({
  open,
  onOpenChange,
  onClose,
  persistent = true,
  title = '',
  width = 572,
  resizable = false,
  minWidth,
  maxWidth,
  onWidthChange,
  footerLeft,
  button,
  children,
  className,
  style,
}: SDrawerProps) {
  const [shaking, setShaking] = useState(false);
  const [rendered, setRendered] = useState(() => Boolean(open));
  const [entered, setEntered] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const backdropPointerDownRef = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const stackRegistrationRef = useRef<ReturnType<typeof registerModalStackEntry> | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const [layerIndex, setLayerIndex] = useState<number | null>(null);
  const [currentWidth, setCurrentWidth] = useState<CSSProperties['width']>(width);
  const [resizing, setResizing] = useState(false);
  // 조절선이 알리는 현재 값. width 가 '50%' 같은 문자열일 수 있어 실제 폭을 재서 쓴다.
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  // 드로어 폭의 절대 상한. 서버에서는 알 수 없어 undefined 다.
  const [viewportWidth, setViewportWidth] = useState<number | undefined>(() =>
    typeof window === 'undefined' ? undefined : window.innerWidth,
  );

  useEffect(() => {
    setCurrentWidth(width);
  }, [width]);

  useEffect(() => {
    if (open) {
      setRendered(true);
      return;
    }
    if (!rendered) return;
    const timer = window.setTimeout(() => setRendered(false), CLOSE_ANIMATION_DURATION);
    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  // 드래그 도중 언마운트되면 document 리스너와 body 스타일이 남는다.
  useEffect(() => {
    return () => resizeCleanupRef.current?.();
  }, []);

  // 창이 줄면 드로어도 따라 줄어야 한다 — 폭·한계를 창 폭에서 자르려면 그 값을 계속 알아야 한다.
  useEffect(() => {
    if (!rendered) return;
    const sync = () => setViewportWidth(window.innerWidth);
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [rendered]);

  // 조절선의 aria-valuenow 는 실제 폭이어야 한다 — 드래그뿐 아니라 %·vw 폭이 창 크기를 따라
  // 움직일 때도 맞아야 하므로 패널을 관찰한다. 조절선이 없으면 잴 이유도 없다.
  useEffect(() => {
    const panel = panelRef.current;
    if (!resizable || !rendered || !panel || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() =>
      setMeasuredWidth(Math.round(panel.getBoundingClientRect().width)),
    );
    observer.observe(panel);
    return () => observer.disconnect();
  }, [resizable, rendered]);

  // 처음 open=true 로 마운트될 때도 닫힌 위치에서 시작해 오른쪽에서 슬라이드 인한다.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }

    setEntered(false);
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const registration = registerModalStackEntry();
    stackRegistrationRef.current = registration;
    setLayerIndex(registration.layerIndex);
    return () => {
      registration.unregister();
      if (stackRegistrationRef.current === registration) stackRegistrationRef.current = null;
    };
  }, [open]);

  const triggerShake = useCallback(() => {
    setShaking(false);
    requestAnimationFrame(() => setShaking(true));
  }, []);

  const guardClose = persistent
    ? (event: Event) => {
        event.preventDefault();
        if (stackRegistrationRef.current?.isTop()) triggerShake();
      }
    : undefined;

  // 토스트·로딩은 드로어보다 위에 뜨지만 DOM 상으로는 body 직속이라 Radix 는 '드로어 바깥'으로
  // 본다. 그대로 두면 토스트 닫기 버튼을 누른 것만으로 드로어가 닫히거나 흔들린다.
  const guardInteractOutside = (e: Event) => {
    if (isFromGlobalOverlay(e)) {
      e.preventDefault();
      return;
    }
    guardClose?.(e);
  };

  const handleBackdropInteraction = () => {
    if (persistent) {
      if (stackRegistrationRef.current?.isTop()) triggerShake();
      return;
    }
    onOpenChange?.(false);
  };

  // ── 너비 조절 ──
  // 창이 드로어보다 좁으면 창 폭에서 멈춘다. 상한이 없으면 창 폭이 곧 상한이고, 하한도 창 폭을
  // 넘지 못한다 — CSS 는 min-width 가 max-width 를 이기므로 하한을 두면 화면 밖으로 삐져나간다.
  // %·vw 로 준 한계는 창 크기를 알아야 px 이 되므로, 창 폭이 바뀔 때마다 다시 푼다.
  const resizeBounds = () => {
    const limit = viewportWidth;
    // 서버에서는 창을 알 수 없다 — 잴 화면도 없으므로 자르지 않고 넘긴다.
    if (limit === undefined) return { min: undefined, max: undefined };
    const min = toPx(minWidth, limit);
    const max = toPx(maxWidth, limit);
    return {
      min: min === undefined ? undefined : Math.min(min, limit),
      max: max === undefined ? limit : Math.min(max, limit),
    };
  };

  /** 조절된 폭을 화면에 반영하고, 시작값과 달라졌을 때만 한 번 알린다. */
  const commitWidth = (next: number, startWidth: number) => {
    const { min, max } = resizeBounds();
    const clamped = Math.round(clamp(next, min, max));
    setCurrentWidth(clamped);
    if (clamped !== Math.round(startWidth)) onWidthChange?.(clamped);
  };

  const handleResizeStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (typeof document === 'undefined') return;
    const panel = panelRef.current;
    if (!panel) return;

    // preventDefault 로 텍스트 선택을 막으면 포커스도 따라오지 않는다.
    // 드래그를 놓은 자리에서 바로 방향키로 이어갈 수 있도록 직접 옮겨 준다.
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus();

    const startX = event.clientX;
    const startWidth = panel.getBoundingClientRect().width;
    const { min, max } = resizeBounds();
    let latest = startWidth;

    const { body } = document;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;

    const onMove = (moveEvent: MouseEvent) => {
      // 드로어는 오른쪽에 붙어 있다 — 손을 왼쪽으로 끌수록 넓어진다.
      latest = Math.round(clamp(startWidth + (startX - moveEvent.clientX), min, max));
      setCurrentWidth(latest);
    };

    const cleanupResize = () => {
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', cleanupResize);
      resizeCleanupRef.current = null;
      setResizing(false);
      // 드래그 중에는 알리지 않는다 — 앱이 폭을 저장한다면 프레임마다 저장하게 된다.
      commitWidth(latest, startWidth);
    };

    resizeCleanupRef.current?.();
    resizeCleanupRef.current = cleanupResize;
    // 본문 텍스트가 드래그로 선택되지 않게, 커서가 페이지 위에서 바뀌지 않게 잠근다.
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanupResize);
    setResizing(true);
  };

  const handleResizeKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const panel = panelRef.current;
    if (!panel) return;
    const startWidth = panel.getBoundingClientRect().width;
    const { min, max } = resizeBounds();
    const step = RESIZE_KEY_STEP[event.shiftKey ? 'large' : 'normal'];

    let next: number | null = null;
    // 조절선이 실제로 움직이는 방향을 따른다 — 왼쪽으로 밀면 드로어가 넓어진다.
    if (event.key === 'ArrowLeft') next = startWidth + step;
    else if (event.key === 'ArrowRight') next = startWidth - step;
    // 한계를 주지 않았으면 끝으로 보낼 자리가 없다.
    else if (event.key === 'Home' && min !== undefined) next = min;
    else if (event.key === 'End' && max !== undefined) next = max;
    if (next === null) return;

    event.preventDefault();
    commitWidth(next, startWidth);
  };

  const modalHost = getModalHost();

  // 조절선이 알리는 값. 잰 폭이 없으면(관찰 전 첫 렌더·ResizeObserver 없음) 숫자로 준 폭을 쓰고,
  // 그마저 문자열이면 알릴 수 있는 값이 없다.
  const resizeValueNow =
    measuredWidth ?? (typeof currentWidth === 'number' ? Math.round(currentWidth) : undefined);
  const { min: resizeMinPx, max: resizeMaxPx } = resizeBounds();
  const resizeValueMin = resizeMinPx === undefined ? undefined : Math.round(resizeMinPx);
  const resizeValueMax = resizeMaxPx === undefined ? undefined : Math.round(resizeMaxPx);

  // 창보다 넓은 폭·하한은 그대로 두면 드로어가 화면 밖으로 나간다 — CSS 는 min-width 가
  // max-width 를 이기므로 max-w-full 만으로는 막히지 않는다. 창 폭에서 자른 값을 직접 넣는다.
  const appliedWidth =
    typeof currentWidth === 'number' && viewportWidth !== undefined
      ? Math.min(currentWidth, viewportWidth)
      : currentWidth;
  const appliedMinWidth = resizeMinPx ?? minWidth;
  const appliedMaxWidth = resizeMaxPx ?? maxWidth;

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal container={modalHost} forceMount={rendered ? true : undefined}>
        <div className="pointer-events-none absolute inset-0" style={{ zIndex: layerIndex ?? 0 }}>
          <RDialog.Overlay
            forceMount={rendered ? true : undefined}
            className="pointer-events-auto absolute inset-0 bg-black/40 data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out"
            style={{ zIndex: 0, animationFillMode: 'forwards' }}
            onPointerDown={event => {
              event.preventDefault();
              event.stopPropagation();
              backdropPointerDownRef.current = true;
              handleBackdropInteraction();
            }}
            onClick={event => {
              event.stopPropagation();
              if (backdropPointerDownRef.current) {
                backdropPointerDownRef.current = false;
                return;
              }
              handleBackdropInteraction();
            }}
          />
          <RDialog.Content
            forceMount={rendered ? true : undefined}
            ref={contentRef}
            className="pointer-events-none absolute inset-y-0 right-0 flex w-fit max-w-full justify-end outline-none"
            onOpenAutoFocus={event => {
              event.preventDefault();
              contentRef.current?.focus({ preventScroll: true });
            }}
            onEscapeKeyDown={guardClose}
            onPointerDownOutside={guardInteractOutside}
            onInteractOutside={guardInteractOutside}
          >
            <RDialog.Title className="sr-only">{title || 'Drawer'}</RDialog.Title>
            <div
              ref={panelRef}
              className={cn(
                'pointer-events-auto relative flex h-full max-w-full translate-x-full flex-col overflow-hidden bg-[var(--cmp-drawer-bg)] shadow-[var(--shadow-overlay)] transition-transform duration-300 ease-out data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full',
                shaking && 'animate-drawer-shake',
                className,
              )}
              data-s-drawer-panel
              data-state={open && entered ? 'open' : 'closed'}
              style={{
                width: appliedWidth,
                minWidth: appliedMinWidth,
                maxWidth: appliedMaxWidth,
                ...style,
              }}
              onAnimationEnd={event => {
                if (event.animationName === 'drawer-shake') setShaking(false);
              }}
            >
              {resizable && (
                <>
                  {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex --
                      포커스 가능한 separator = ARIA window splitter 로, 값을 가지는 위젯이다.
                      jsx-a11y 는 separator 를 항상 비대화형으로 보므로 여기서만 끈다. */}
                  <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Drawer 너비 조절"
                    aria-valuenow={resizeValueNow}
                    aria-valuemin={resizeValueMin}
                    aria-valuemax={resizeValueMax}
                    aria-valuetext={
                      resizeValueNow === undefined ? undefined : `${resizeValueNow}px`
                    }
                    tabIndex={0}
                    onMouseDown={handleResizeStart}
                    onKeyDown={handleResizeKeyDown}
                    className="group absolute inset-y-0 left-0 z-[1] flex cursor-col-resize touch-none justify-start outline-none"
                    style={{ width: RESIZE_HIT_WIDTH }}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        // 평상시엔 자리만 잡고 칠하지 않는다 — 색이 붙는 것 자체가 조절 가능하다는 신호다.
                        'w-(--cmp-splitter-border-width) transition-colors',
                        'group-hover:bg-(--cmp-splitter-border-color) group-focus-visible:bg-(--cmp-splitter-border-color)',
                        resizing && 'bg-(--cmp-splitter-border-color)',
                      )}
                    />
                  </div>
                  {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
                </>
              )}
              <SOverlayHeader
                title={title}
                showClose
                onClose={() => {
                  onClose?.();
                  onOpenChange?.(false);
                }}
              />

              <div className="min-h-0 flex-1 overflow-auto border-t border-solid border-[color:var(--cmp-drawer-body-border)]">
                <InsideModalProvider value={true}>
                  <PortalContainerProvider value={portalContainer}>
                    {children}
                  </PortalContainerProvider>
                </InsideModalProvider>
              </div>

              {(button || footerLeft) && (
                <SFooter left={footerLeft} leftClassName="justify-between" button={button} />
              )}
            </div>
            <div
              ref={setPortalContainer}
              className="pointer-events-auto fixed left-0 top-0"
              style={{ zIndex: 2 }}
            />
          </RDialog.Content>
        </div>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
